'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { useApiFetch } from '@/features/auth'
import {
  createInvestor,
  fetchInvestors,
  type CreateInvestorInput,
} from '@/features/investors/api/investors.api'
import type { InvestorsListParams } from '@/features/investors/types'
import { AppError } from '@/lib/errors/appError'
import { queryKeys } from '@/lib/queryKeys'

function toastError(error: unknown) {
  toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
}

export function useInvestors(params: InvestorsListParams) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.investorsResource.list(params),
    queryFn: () => fetchInvestors(authedFetch, params),
  })
}

export function useCreateInvestor() {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateInvestorInput) => createInvestor(authedFetch, input),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'investors-resource' && q.queryKey[1] === 'list',
      })
      // `POST /investors` never 409s on a duplicate PAN/mobile — it matches the existing row
      // instead. This is a UX-only heuristic (not a guarantee) to tell the two outcomes apart in
      // the toast; being wrong about it costs nothing since the underlying operation is correct
      // either way.
      const justCreated = Date.now() - new Date(created.createdAt).getTime() < 5_000
      toast({
        variant: 'success',
        title: justCreated
          ? 'Investor created'
          : 'Matched an existing investor by PAN/mobile — no duplicate created',
      })
    },
    onError: toastError,
  })
}
